import React from 'react';
import { BottomSheet, FilterChip, Textarea, Checkbox, Button, Badge, Alert } from '@parsina/framework';
import { C } from '../../constants/theme';
import { LTR_TEXT } from '../../i18n/I18nProvider.jsx';
import {
  CONDITION_OPTIONS, PRESENCE, PRESENCE_OPTIONS, SURFACE_STATE_OPTIONS,
  isMissing, materialsFor, surfaceColor, surfacesFor,
} from '../../lib/teeth.js';
import { buildToothEvents } from '../../lib/toothDraft.js';

// Editing one tooth, on a phone.
//
// The desktop editor is the kit `Drawer` at a fixed `width: 400; left: 0` — on a
// 320–390px phone it exceeds the viewport, and DentistLayout's `overflow:hidden`
// clips whatever spills. Its pill row is a hand-rolled raw <button> at ~30px,
// beside a raw <textarea> and a raw <input type=checkbox>. None of that is
// copied here: §6's floor is 2.75rem and DESIGN.md rule 1 forbids a raw
// <button>, so every control below is a kit primitive — `FilterChip` (min-h-11,
// aria-pressed, data-state), `Textarea`, `Checkbox`, `Button`.
//
// THE SURFACE PROBLEM IS SOLVED HERE. A surface region inside the arch is well
// under 10px on a phone and cannot be enlarged without breaking the anatomical
// frame (CORE.md §9). Every surface is therefore its own 44px chip in this
// sheet, and the chart's own surface taps stay a convenience for a stylus or a
// tablet.
//
// The DRAFT and the batch come from `buildToothEvents` — the same function the
// desktop drawer builds its batch with. The markup forks; the wire format does
// not (§1.1).

const Section = ({ title, hint, children }) => (
  <div style={{ marginBottom: '1.125rem' }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.5rem' }}>
      <h4 style={{ margin: 0, fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>
        {title}
      </h4>
      {hint && <span style={{ fontSize: '0.6875rem', color: C.textSecondary }}>{hint}</span>}
    </div>
    {children}
  </div>
);

// §6: "adjacent targets keep ≥ 0.5rem gap".
const ChipRow = ({ children }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>{children}</div>
);

export default function MobileToothSheet({
  open, fdi, state, onClose, onSave, saving, focusSurface, t,
  /**
   * What has already happened to this tooth, rendered above the editor.
   *
   * A NODE, for the same reason the desktop drawer takes one: the screen knows
   * what the trail costs and when to read it; the sheet only decides WHERE it
   * goes — first, because «what was done here» is the context for what is about
   * to be recorded.
   */
  history = null,
}) {
  // draft: { presence, condition:{value,material}, surfaces:{ [code]:{value,material} }, note }
  const [draft, setDraft] = React.useState({ surfaces: {} });
  const [activeSurface, setActiveSurface] = React.useState(null);
  const [err, setErr] = React.useState(null);

  // Reset whenever the sheet opens on a different tooth — a draft carried
  // between teeth would record a filling on whichever one was opened next.
  React.useEffect(() => {
    if (open) {
      setDraft({ surfaces: {} });
      setErr(null);
      setActiveSurface(focusSurface || null);
    }
  }, [open, fdi, focusSurface]);

  const surfaces = state?.surfacesAvailable?.length
    ? state.surfacesAvailable
    : (fdi ? surfacesFor(fdi) : []);
  const missing = isMissing(draft.presence ?? state?.presence);
  const currentOf = (code) =>
    (state?.surfaces || []).find((s) => s.surface === code)?.value || null;

  const setSurface = (code, value) => setDraft((d) => ({
    ...d,
    surfaces: {
      ...d.surfaces,
      // Tapping the chosen value again clears the pending edit rather than
      // recording the same thing twice.
      [code]: d.surfaces[code]?.value === value ? undefined : { value, material: null },
    },
  }));

  const setSurfaceMaterial = (code, material) => setDraft((d) => ({
    ...d,
    surfaces: { ...d.surfaces, [code]: { ...d.surfaces[code], material } },
  }));

  // EVERY HOOK IS ABOVE THIS LINE — the same rule the desktop drawer carries.
  // React identifies hooks by call order, so an early return between two of
  // them is never safe however obvious the condition looks (React #310).
  const events = React.useMemo(
    () => buildToothEvents(draft, fdi, state),
    [draft, fdi, state],
  );

  if (!open || !fdi) return null;

  const submit = async () => {
    setErr(null);
    const failure = await onSave(events);
    // Losing a half-charted tooth to a closed sheet is worse than the error.
    if (failure) setErr(failure);
    else onClose();
  };

  const title = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
      <span style={{ color: C.navy, fontWeight: 800 }}>
        {t('chart.tooth')} <span {...LTR_TEXT}>{fdi}</span>
      </span>
      {state?.universal && (
        <span style={{ fontSize: '0.6875rem', fontWeight: 500, color: C.textSecondary }}>
          {t('chart.universalShort')} <span {...LTR_TEXT}>{state.universal}</span>
        </span>
      )}
      {state?.isCharted === false && <Badge>{t('chart.notCharted')}</Badge>}
    </span>
  );

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={title}
      closeLabel={t('mobile.close')}
      data-testid="tooth-sheet"
      footer={(
        // §6: "submit docks bottom". The sheet's body is an 85vh scroll region,
        // so a commit row inside it would scroll away exactly when it is needed.
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            onClick={submit}
            disabled={saving || events.length === 0}
            data-testid="tooth-sheet-save"
            className="flex-1 min-h-11"
          >
            {saving
              ? t('common.actions.saving')
              : `${t('common.actions.save')}${events.length ? ` (${events.length})` : ''}`}
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            data-testid="tooth-sheet-cancel"
            className="min-h-11"
          >
            {t('common.actions.cancel')}
          </Button>
        </div>
      )}
    >
      {history}

      <Section title={t('chart.presence.title')}>
        <ChipRow>
          {PRESENCE_OPTIONS.map((p) => {
            const on = (draft.presence ?? state?.presence) === p;
            return (
              <FilterChip
                key={p}
                label={t(`chart.presence.${p}`)}
                active={on}
                onClick={() => setDraft((d) => ({ ...d, presence: p }))}
                data-testid={`tooth-sheet-presence-${p}`}
                style={on && p === PRESENCE.PRESENT ? { borderColor: C.teal } : undefined}
              />
            );
          })}
        </ChipRow>
      </Section>

      {/* A tooth that is not in the mouth has no surfaces to fill. Hiding the
          editor is not cosmetic — the server rejects such an event, so offering
          it would only produce an error the dentist cannot act on. */}
      {!missing && (
        <>
          <Section title={t('chart.surfaces.title')} hint={t('chart.surfaces.hint')}>
            <ChipRow>
              {surfaces.map((code) => {
                const pending = draft.surfaces[code]?.value;
                const current = currentOf(code);
                const shown = pending || current;
                return (
                  <FilterChip
                    key={code}
                    active={activeSurface === code}
                    onClick={() => setActiveSurface(activeSurface === code ? null : code)}
                    data-testid={`tooth-sheet-surface-${code}`}
                    style={shown ? { borderColor: surfaceColor(shown).stroke } : undefined}
                  >
                    <span {...LTR_TEXT}>{code}</span>
                    {' · '}
                    {t(`chart.surface.${code}`)}
                    {shown && (
                      <span style={{ marginInlineStart: '0.375rem', fontSize: '0.6875rem', opacity: 0.85 }}>
                        {t(`chart.surfaceState.${shown}`)}
                        {pending && ' *'}
                      </span>
                    )}
                  </FilterChip>
                );
              })}
            </ChipRow>

            {activeSurface && (
              <div
                style={{
                  marginTop: '0.625rem',
                  padding: '0.75rem',
                  borderRadius: '0.625rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                }}
              >
                <div style={{ fontSize: '0.75rem', color: C.textSecondary, marginBottom: '0.5rem' }}>
                  {t('chart.surfaces.setFor')} <b>{t(`chart.surface.${activeSurface}`)}</b>
                </div>
                <ChipRow>
                  {SURFACE_STATE_OPTIONS.map((v) => (
                    <FilterChip
                      key={v}
                      label={t(`chart.surfaceState.${v}`)}
                      active={draft.surfaces[activeSurface]?.value === v}
                      onClick={() => setSurface(activeSurface, v)}
                      data-testid={`tooth-sheet-state-${v}`}
                      style={{ borderColor: surfaceColor(v).stroke }}
                    />
                  ))}
                </ChipRow>

                {materialsFor('surface', draft.surfaces[activeSurface]?.value).length > 0 && (
                  <div style={{ marginTop: '0.625rem' }}>
                    <div style={{ fontSize: '0.75rem', color: C.textSecondary, marginBottom: '0.375rem' }}>
                      {t('chart.material')}
                    </div>
                    <ChipRow>
                      {materialsFor('surface', draft.surfaces[activeSurface]?.value).map((m) => (
                        <FilterChip
                          key={m}
                          label={t(`chart.materialName.${m}`)}
                          active={draft.surfaces[activeSurface]?.material === m}
                          onClick={() => setSurfaceMaterial(activeSurface, m)}
                          data-testid={`tooth-sheet-surface-material-${m}`}
                        />
                      ))}
                    </ChipRow>
                  </div>
                )}
              </div>
            )}
          </Section>

          <Section title={t('chart.condition.title')} hint={t('chart.condition.hint')}>
            <ChipRow>
              {CONDITION_OPTIONS.map((c) => (
                <FilterChip
                  key={c}
                  label={t(`chart.conditionName.${c}`)}
                  active={draft.condition?.value === c}
                  onClick={() => setDraft((d) => ({
                    ...d,
                    condition: d.condition?.value === c ? null : { value: c, material: null },
                  }))}
                  data-testid={`tooth-sheet-condition-${c}`}
                />
              ))}
            </ChipRow>
            {materialsFor('condition', draft.condition?.value).length > 0 && (
              <div style={{ marginTop: '0.625rem' }}>
                <div style={{ fontSize: '0.75rem', color: C.textSecondary, marginBottom: '0.375rem' }}>
                  {t('chart.material')}
                </div>
                <ChipRow>
                  {materialsFor('condition', draft.condition?.value).map((m) => (
                    <FilterChip
                      key={m}
                      label={t(`chart.materialName.${m}`)}
                      active={draft.condition?.material === m}
                      onClick={() => setDraft((d) => ({
                        ...d, condition: { ...d.condition, material: m },
                      }))}
                      data-testid={`tooth-sheet-condition-material-${m}`}
                    />
                  ))}
                </ChipRow>
              </div>
            )}
          </Section>
        </>
      )}

      <Section title={t('chart.note.title')}>
        <Textarea
          value={draft.note || ''}
          onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
          rows={2}
          dir="auto"
          placeholder={t('chart.note.placeholder')}
          aria-label={t('chart.note.title')}
          data-testid="tooth-sheet-note"
          // ≥1rem or iOS zooms the whole page on focus (§6). `inputStyle`
          // reaches the control; the kit's `style` dresses the field.
          inputStyle={{ fontSize: '1rem' }}
        />
      </Section>

      {/* Historical vs done-here. Without it, charting a new patient's
          existing work looks like a day of treatment. */}
      <Checkbox
        checked={!!draft.isHistorical}
        onChange={(next) => setDraft((d) => ({ ...d, isHistorical: !!next }))}
        label={t('chart.isHistorical')}
        data-testid="tooth-sheet-historical"
        className="min-h-11"
      />

      {err && (
        <Alert variant="error" role="alert" style={{ marginTop: '0.875rem' }}>
          {err.message}
        </Alert>
      )}
    </BottomSheet>
  );
}
